import { Suspense, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { Alert } from '@mui/material';
import {
  type RentalComparable,
  toRentalComparable,
} from '@realadvisor/listings/rental-cma';

import type { Ch_Rental_Cma_Listings_Distance_Bool_Exp } from '@graphql/__generated__/graphql';
import { SchemaInitializer } from '@modules/_shared/components/filters/SchemaProvider';
import { ListToolbar } from '@modules/_shared/components/list-toolbar/ListToolbar';
import RaGrid from '@modules/_shared/components/RaGrid';
import ScraperListingCard from '@modules/_shared/components/scraper-listing-card/ScraperListingCard';
import { usePropertyTypeOptionRenderer } from '@modules/_shared/hooks/usePropertyTypeOptionRenderer';
import { useScopedSearchParams } from '@modules/_shared/utils/navigation';
import {
  MAX_GRAPHQL_INT,
  getPaginationVariables,
} from '@modules/_shared/utils/paginations';
import { prepareWhereClauseQuery } from '@modules/_shared/utils/parseWhereClause';
import ComparableListingsSkeleton from '@modules/comparable-listings/ComparableListingsSkeleton';
import UrlSearch from '@modules/comparable-listings/UrlSearch';
import {
  RENTAL_CMA_LISTINGS_COUNT,
  RENTAL_CMA_MANUAL_LISTINGS,
} from '@modules/reports/cma-reports/rentalQueries';
import { useLocale } from '@shared/hooks/locale';

import { getRentalCountryVariables } from './rentalComparablesSearch';
import { SelectableCard } from './SelectableCard';

const scope = 'rental_cma';
const pageSize = 24;

export const RentalComparableListings = ({
  selection,
  onSelect,
  onDetails,
  disabled,
  lat,
  lng,
  stateId,
}: {
  selection: RentalComparable[];
  onSelect: (item: RentalComparable) => void;
  onDetails: (item: RentalComparable) => void;
  disabled: boolean;
  lat: number;
  lng: number;
  stateId: string | null;
}) => {
  const { t, countryCode } = useLocale();
  const renderPropertyType = usePropertyTypeOptionRenderer();
  const [searchParams] = useScopedSearchParams(scope);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchMessage, setSearchMessage] = useState<string | null>(null);
  const pins = searchParams.get('pinned_listing_id');
  const ids = pins
    ?.split(',')
    .map(Number)
    .filter(id => Number.isInteger(id) && id >= 0 && id <= MAX_GRAPHQL_INT);
  const filters: Ch_Rental_Cma_Listings_Distance_Bool_Exp = JSON.parse(
    searchParams.get('where') || '{}',
  );
  const { locality_object, ...listingFilters } = filters.listing ?? {};
  const where: Ch_Rental_Cma_Listings_Distance_Bool_Exp = ids?.length
    ? { listing_id: { _in: ids } }
    : {
        ...filters,
        listing: {
          _and: [
            prepareWhereClauseQuery(listingFilters),
            prepareWhereClauseQuery(
              locality_object?.id?._in
                ? { places: locality_object }
                : { locality_object },
            ),
          ],
        },
      };
  const { offset } = getPaginationVariables(searchParams);
  const effectiveStateId = ids?.length ? null : stateId;
  const { data, loading, error, refetch } = useQuery(
    RENTAL_CMA_MANUAL_LISTINGS,
    {
      variables: {
        lat,
        lng,
        stateId: effectiveStateId,
        limit: pageSize,
        offset,
        ...getRentalCountryVariables(countryCode, where),
      },
      context: { clientName: 'scrapers' },
      notifyOnNetworkStatusChange: true,
    },
  );
  const {
    data: countData,
    loading: countLoading,
    error: countError,
  } = useQuery(RENTAL_CMA_LISTINGS_COUNT, {
    variables: {
      lat,
      lng,
      stateId: effectiveStateId,
      ...getRentalCountryVariables(countryCode, where),
    },
    context: { clientName: 'scrapers' },
  });
  const rows = (
    data?.ch_get_rental_cma_listings_distance ??
    data?.es_get_rental_cma_listings_distance ??
    data?.fr_get_rental_cma_listings_distance ??
    data?.it_get_rental_cma_listings_distance ??
    data?.pl_get_rental_cma_listings_distance ??
    data?.pt_get_rental_cma_listings_distance ??
    []
  ).flatMap(({ listing, distance_in_meters }) => {
    if (!listing) {
      return [];
    }
    const comparable = toRentalComparable(listing);
    return comparable
      ? [{ id: listing.id, listing, comparable, distance_in_meters }]
      : [];
  });
  const tableName = `${countryCode.toLowerCase()}_rental_cma_listings_distance`;
  return (
    <Suspense
      fallback={
        <RaGrid
          loading
          renderSkeleton={() => <ComparableListingsSkeleton />}
          renderItemCard={() => null}
        />
      }
    >
      <SchemaInitializer isScraperSchema />
      <ListToolbar
        isScraperSchema
        tableName={tableName}
        queryParamsScope={scope}
        refetch={refetch}
        quickFilters={[
          {
            label: t('Location'),
            path: [`${tableName}_bool_exp`, 'listing', 'locality_object'],
          },
          {
            label: t('Distance (meters)'),
            path: [`${tableName}_bool_exp`, 'distance_in_meters'],
          },
          {
            label: t('Online'),
            path: [`${tableName}_bool_exp`, 'is_online'],
          },
          {
            label: t('Property type'),
            path: [
              `${tableName}_bool_exp`,
              'listing',
              'property_main_type',
              '_in',
            ],
            filterAutocompleteRenderOption: renderPropertyType,
          },
          {
            label: t('Surfaces'),
            path: [`${tableName}_bool_exp`, 'listing', 'computed_surface'],
          },
          {
            label: t('Monthly net rent'),
            path: [`${tableName}_bool_exp`, 'listing', 'rent_net_monthly'],
          },
          {
            label: t('Rooms'),
            path: [`${tableName}_bool_exp`, 'listing', 'number_of_rooms'],
          },
          {
            label: t('Price per m²'),
            path: [`${tableName}_bool_exp`, 'net_rent_per_sqm'],
          },
          {
            label: t('Land surface'),
            path: [`${tableName}_bool_exp`, 'listing', 'land_surface'],
          },
          {
            label: t('Construction year'),
            path: [`${tableName}_bool_exp`, 'listing', 'construction_year'],
          },
          {
            label: t('Renovation year'),
            path: [`${tableName}_bool_exp`, 'listing', 'renovation_year'],
          },
        ]}
        searchComponent={
          <UrlSearch
            offerType="rent"
            queryParamsScope={scope}
            onEmptyMessageChange={setSearchMessage}
            onLoadingChange={setSearchLoading}
          />
        }
      />
      {(error || countError) && (
        <Alert severity="error" sx={{ m: 2 }}>
          {t('Could not load rental comparisons. Please try again.')}
        </Alert>
      )}
      <RaGrid
        items={rows}
        loading={loading || searchLoading}
        loadingCount={countLoading}
        totalCount={
          (
            countData?.ch_get_rental_cma_listings_distance_aggregate ??
            countData?.es_get_rental_cma_listings_distance_aggregate ??
            countData?.fr_get_rental_cma_listings_distance_aggregate ??
            countData?.it_get_rental_cma_listings_distance_aggregate ??
            countData?.pl_get_rental_cma_listings_distance_aggregate ??
            countData?.pt_get_rental_cma_listings_distance_aggregate
          )?.aggregate?.count ?? 0
        }
        pageSize={pageSize}
        queryParamsScope={scope}
        emptyMessage={searchMessage ?? undefined}
        renderSkeleton={() => <ComparableListingsSkeleton />}
        renderItemCard={({ listing, comparable, distance_in_meters }) => (
          <SelectableCard
            selected={selection.some(item => item.id === comparable.id)}
            onChange={() => {
              if (!disabled) {
                onSelect(comparable);
              }
            }}
          >
            <ScraperListingCard
              listing={listing}
              rentalPrice={comparable}
              addressLabel={comparable.address}
              variant="vertical"
              showPricePerSqm
              showDaysOnMarket
              showOfflineSince
              showDistance
              distanceInMeters={distance_in_meters}
              onClick={() => onDetails(comparable)}
            />
          </SelectableCard>
        )}
      />
    </Suspense>
  );
};
